import * as React from "react";
import { IStyleFunctionOrObject, ITooltipHostProps, ITooltipHostStyleProps, ITooltipHostStyles, mergeStyles } from "@fluentui/react";

/**
 * Gets a native tooltip string when the content is plain text.
 *
 * @param content - The tooltip content.
 * @returns The text to use for the native title attribute.
 */
const getNativeTooltipText = (content: ITooltipHostProps["content"]): string | undefined => {
  return typeof content === "string" ? content : undefined;
};

/**
 * Resolves a host class name for a native tooltip wrapper.
 *
 * @param styles - Tooltip host styles.
 * @param hostClassName - The provided host class name.
 * @returns The resolved class name.
 */
const getHostClassName = (
  styles: IStyleFunctionOrObject<ITooltipHostStyleProps, ITooltipHostStyles> | undefined,
  hostClassName: string | undefined
): string | undefined => {
  if (!styles || typeof styles === "function") {
    return hostClassName;
  }

  return mergeStyles(styles.root, hostClassName);
};

/**
 * Renders a native tooltip wrapper instead of Fluent UI TooltipHost.
 * This avoids a React 18 timing issue where TooltipHost can receive
 * focus before its internal async helper is initialized.
 *
 * @param props - The tooltip host props.
 * @returns A native title-based tooltip wrapper.
 */
export const DeferredTooltipHost: React.FC<ITooltipHostProps> = (props) => {
  const { children, content, hostClassName, id, styles } = props;
  const className = getHostClassName(styles, hostClassName);

  return (
    <div className={className} id={id} title={getNativeTooltipText(content)}>
      {children}
    </div>
  );
};
